/**
 * Le classement de la semaine, en démo — une équipe FICTIVE, et le compte de
 * tournage parmi elle.
 *
 * Kayla, 15-10-2026 : en démo, l'onglet Classement et la pastille de rang
 * lisaient le VRAI `classement.semaine` — les pseudos, les avatars et les
 * gains des vrais chatteurs passaient à l'image d'une vidéo de formation.
 *
 * La place du compte de tournage se lit dans l'argent de la démo (`argent.ts`,
 * UNE source) : ses ventes depuis lundi 12h. L'équipe est figée au premier
 * calcul, et un coéquipier l'attend juste devant — une vente pendant la prise
 * le dépasse, et la pastille fête la montée comme en vrai.
 */
import { primesDuPodium } from "@/../convex/_lib/classement";
import type { ClassementData } from "@/panels/personnel/classement/ClassementOnglet";
import { lastUnlockMs } from "@/panels/organisation/payoutCycle";

import { lesVentes } from "./argent";

/** Les coéquipiers : des prénoms inventés, aucun chatteur réel. */
const EQUIPE = ["Lina", "Sami", "Jade", "Milo", "Eva", "Adam", "Zoé", "Nora"];

let equipe: Array<{ nom: string; brutCents: number }> | null = null;

/** Ce que le compte de tournage a vendu depuis lundi 12h (en cents de dollar). */
function monBrut(maintenant: number): number {
  const debut = lastUnlockMs(maintenant);
  return lesVentes(maintenant)
    .filter((v) => Date.parse(v.created_at) >= debut)
    .reduce((s, v) => s + v.gross_usd_cents, 0);
}

/**
 * L'équipe, posée sur le brut de départ : trois devant de loin, un JUSTE
 * devant (40 $ — une vente de 50 € le dépasse), le reste derrière.
 */
function lEquipe(depart: number): Array<{ nom: string; brutCents: number }> {
  if (equipe) return equipe;
  const ecarts = [2.4, 1.9, 1.5];
  equipe = EQUIPE.map((nom, i) => {
    if (i < ecarts.length) return { nom, brutCents: Math.round((depart * ecarts[i]!) / 100) * 100 + 4_000 };
    if (i === ecarts.length) return { nom, brutCents: depart + 4_000 };
    return { nom, brutCents: Math.max(1_000, Math.round(depart * (0.8 - 0.12 * (i - 4)) / 100) * 100) };
  });
  return equipe;
}

export function leClassement(
  moi: { workosUserId: string; nom: string; avatarUrl: string | null },
  maintenant = Date.now(),
): ClassementData {
  const mien = monBrut(maintenant);
  const lignesBrutes = [
    ...lEquipe(mien).map((e, i) => ({ workosUserId: `demo-chatteur-${i}`, nom: e.nom, brutCents: e.brutCents, moi: false })),
    { workosUserId: moi.workosUserId, nom: moi.nom, brutCents: mien, moi: true },
  ].sort((a, b) => b.brutCents - a.brutCents);

  const total = lignesBrutes.reduce((s, l) => s + l.brutCents, 0);
  const primes = primesDuPodium(total);
  const lignes = lignesBrutes.map((l, i) => ({
    primeCents: i < 3 ? primes[i]! : 0,
    exAequo: false,
    rang: i + 1,
    workosUserId: l.workosUserId,
    nom: l.nom,
    avatarUrl: l.moi ? moi.avatarUrl : null,
    moi: l.moi,
    brutCents: l.brutCents,
  }));

  const lundi = new Date(lastUnlockMs(maintenant)).toISOString().slice(0, 10);
  return {
    cycle: `demo-${lundi}`,
    misAJourLe: maintenant,
    primes,
    lignes,
    monRang: lignes.find((l) => l.moi)?.rang ?? null,
    semaineDerniere: [
      { rang: 1, montantCents: 3_120, commissionCents: 15_600, ventes: 21, nom: "Jade", avatarUrl: null, moi: false, cache: false },
      { rang: 2, montantCents: 1_870, commissionCents: 9_350, ventes: 14, nom: "Lina", avatarUrl: null, moi: false, cache: false },
      { rang: 3, montantCents: 1_250, commissionCents: 6_200, ventes: 9, nom: "Sami", avatarUrl: null, moi: false, cache: false },
    ],
  };
}

/** Repart de zéro (nouveau monde, et le banc). */
export function oublierLeClassement(): void {
  equipe = null;
}
